import type { CleaningTeam, PropertyCalendar } from '@/api/client'
import { SheetKicker } from '@/components/ui/sheet'
import { usePropertyBlocks } from '@/hooks/use-property-blocks'
import { parseCalendarDate } from '@/lib/calendar-date'
import { parseGapOpen } from '@/lib/gap-events'
import { isReservationCancelled } from '@/lib/reservation-utils'
import { parseSupplyOpen } from '@/lib/supply-events'
import { shortDay } from './agenda'
import { BlockCreateSheet, HoldSheet, OwnerBlockSheet, lookupBlock } from './block-sheets'
import type { BlockSheetBack } from './block-sheets'
import {
  findOverlaps,
  newBlockOpenValue,
  overlapNote,
  parseBlockOpen,
  type OverlapSource,
} from './block-utils'
import { CleaningSheet } from './cleaning-sheet'
import { buildDayGroups } from './day-groups'
import { dayOpenValue, parseDayOpen } from './day-list'
import { DaySheet } from './day-sheet'
import { GapSheet } from './gap-sheet'
import { CALENDAR_LAYERS, type CalendarLayerId } from './layers'
import type { CalendarReadiness } from './readiness'
import { depositFor, gapFor, reviewFor, supplyFor, turnoverCleaningFor } from './sheet-lookups'
import {
  cleaningOpenValue,
  parseCleaningOpen,
  parseStayOpen,
  type CalendarSheetNav,
} from './sheet-open'
import { StaySheet } from './stay-sheet'
import { SupplySheet } from './supply-sheet'
import { rangeHolds, type CalendarData, type CalendarRange } from './use-calendar-data'

/**
 * The property calendar's sheets, and what opens them: one implementation for
 * the property Calendar tab and the portfolio calendar (CAP-1418), so a stay,
 * a block, a cleaning, a gap, a supply, a deposit or a review opens the same
 * sheet, with the same details and actions, wherever it is clicked.
 *
 * A page owns its URL: it says what is open and how to open something else
 * (`CalendarSheetNav`), and hands over the read it has for the property
 * (`CalendarData`). What each sheet is told is decided here.
 */

export type DayListState = 'pending' | 'outside' | 'ok'

interface CalendarSheetsProps {
  propertyId: string
  /** Names the cleaning sheet, whose body is the cleaning board's. */
  propertyName: string
  /** The read for the range: the property tab's month read, or the portfolio's windows for the property. */
  calendar: PropertyCalendar | undefined
  /** The read above is the last range's, kept while this one loads. */
  isPlaceholderData: boolean
  readiness: Pick<CalendarReadiness, 'summaries' | 'stale' | 'monthLoading'>
  data: CalendarData
  range: CalendarRange
  isVisible: (id: CalendarLayerId) => boolean
  teams: CleaningTeam[]
  crewLoading: boolean
  blocksQuery: ReturnType<typeof usePropertyBlocks>
  nav: CalendarSheetNav
  /**
   * Where a day stands for its day list. Left out: not read yet while the range
   * loads (or is the last one's), outside past the range, read otherwise.
   */
  dayListState?: (day: string) => DayListState
  /** What the day list says for a day outside the range; the day sheet's own words when left out. */
  outsideMessage?: string
}

/**
 * The sheet for whatever `nav.open` names: a stay, a supply category, a day's
 * list, a block's form or panel, a gap or a cleaning. Nothing is drawn when
 * nothing is open, or for a value it does not know (the page may own others,
 * such as the property tab's reviews-to-leave list).
 */
export function CalendarSheets({
  propertyId,
  propertyName,
  calendar,
  isPlaceholderData,
  readiness,
  data,
  range,
  isVisible,
  teams,
  crewLoading,
  blocksQuery,
  nav,
  dayListState,
  outsideMessage,
}: CalendarSheetsProps) {
  const { open, section, go } = nav
  const { summaries, stale, monthLoading } = readiness
  const openStayId = parseStayOpen(open)
  const openCleaningId = parseCleaningOpen(open)
  const openSupply = parseSupplyOpen(open)
  const openGapNight = parseGapOpen(open)
  const openDay = parseDayOpen(open)
  const openBlockValue = parseBlockOpen(open)
  const close = () => go(null, { replace: true })
  const { stays, timeZone, today, blockPool } = data

  const openStay = openStayId ? (stays.find(s => s.reservation_id === openStayId) ?? null) : null

  // The block sheet's overlap line and its way back to the day list it came from.
  const openPoolBlock =
    openBlockValue && openBlockValue.kind !== 'new'
      ? blockPool.find(
          block =>
            block.kind === (openBlockValue.kind === 'hold' ? 'uplisting' : 'owner') &&
            block.id === openBlockValue.id
        )
      : undefined
  const openBlockOverlapNote = openPoolBlock ? overlapNote(openPoolBlock, blockPool) : null
  const fromDay = nav.fromDay && parseCalendarDate(nav.fromDay) ? nav.fromDay : null
  const blockSheetBack: BlockSheetBack | undefined = fromDay
    ? {
        label: `All blocks on ${shortDay(fromDay)}`,
        onBack: () => go(dayOpenValue(fromDay)),
      }
    : undefined

  const dayState: DayListState = !openDay
    ? 'ok'
    : dayListState
      ? dayListState(openDay)
      : monthLoading || stale
        ? 'pending'
        : rangeHolds(range, openDay)
          ? 'ok'
          : 'outside'
  const dayNotShown = CALENDAR_LAYERS.flatMap(({ id, label }) =>
    !isVisible(id)
      ? [`${label} (switched off)`]
      : summaries[id].status === 'error'
        ? [`${label} (could not be read)`]
        : []
  )

  // What a drafted block runs over, for its note: the read's stays plus every
  // block the blocks read has (any month). Information only.
  const overlapSources = (): OverlapSource[] => [
    ...stays
      .filter(stay => !isReservationCancelled(stay) && stay.check_in && stay.check_out)
      .map(stay => ({
        label: `${stay.guest_name || 'Guest'} reservation`,
        start: stay.check_in as string,
        end: stay.check_out as string,
      })),
    ...(blocksQuery.data?.owner_blocks ?? []).flatMap(block =>
      block.start && block.end ? [{ label: 'Owner block', start: block.start, end: block.end }] : []
    ),
    ...(blocksQuery.data?.external_blocks ?? []).flatMap(hold =>
      hold.start && hold.end
        ? [{ label: 'Blocked in Uplisting', start: hold.start, end: hold.end }]
        : []
    ),
  ]

  return (
    <>
      {openStayId && (
        <StaySheet
          key={openStayId}
          propertyId={propertyId}
          reservationId={openStayId}
          stay={openStay}
          cleaning={turnoverCleaningFor(
            openStayId,
            summaries.cleaning,
            data.cleaningJobs,
            teams,
            crewLoading
          )}
          deposit={depositFor(openStayId, summaries.deposits, data.depositItems)}
          review={reviewFor(openStayId, summaries.reviews, data.reviewItems, today)}
          timeZone={timeZone}
          section={section}
          onOpenCleaning={jobId => go(cleaningOpenValue(jobId))}
          onClose={close}
        />
      )}

      {openSupply && (
        <SupplySheet
          key={`${openSupply.category}:${openSupply.day}`}
          propertyId={propertyId}
          category={openSupply.category}
          day={openSupply.day}
          supply={supplyFor(openSupply, summaries.supplies, data.supplyItems, isPlaceholderData)}
          onClose={close}
        />
      )}

      {openDay && (
        <DaySheet
          key={openDay}
          day={openDay}
          onBlockDates={() => go(newBlockOpenValue(openDay))}
          state={dayState}
          groups={
            dayState === 'ok'
              ? buildDayGroups(
                  {
                    data,
                    range,
                    isVisible,
                    blocksRead: calendar?.layers.blocks.status === 'ok',
                    blocksData: blocksQuery.data,
                    teams,
                    crewLoading,
                    go,
                  },
                  openDay
                )
              : []
          }
          notShown={dayNotShown}
          outsideMessage={outsideMessage}
          onClose={close}
        />
      )}

      {openBlockValue?.kind === 'new' && (
        <BlockCreateSheet
          key={`${openBlockValue.start}:${openBlockValue.end ?? ''}`}
          propertyId={propertyId}
          initialStart={openBlockValue.start}
          initialEnd={openBlockValue.end}
          today={today}
          overlapsFor={(start, end) => findOverlaps(start, end, overlapSources())}
          onClose={close}
        />
      )}

      {openBlockValue?.kind === 'owner' && (
        <OwnerBlockSheet
          key={openBlockValue.id}
          propertyId={propertyId}
          overlapNote={openBlockOverlapNote}
          back={blockSheetBack}
          lookup={lookupBlock(blocksQuery, blocks =>
            blocks.owner_blocks.find(block => block.block_id === openBlockValue.id)
          )}
          onClose={close}
        />
      )}

      {openBlockValue?.kind === 'hold' && (
        <HoldSheet
          key={openBlockValue.id}
          propertyId={propertyId}
          overlapNote={openBlockOverlapNote}
          back={blockSheetBack}
          lookup={lookupBlock(blocksQuery, blocks =>
            blocks.external_blocks.find(hold => hold.id === openBlockValue.id)
          )}
          onClose={close}
        />
      )}

      {openGapNight && (
        <GapSheet
          key={openGapNight}
          firstNight={openGapNight}
          gap={gapFor(openGapNight, calendar, isPlaceholderData)}
          timeZone={timeZone}
          onClose={close}
        />
      )}

      {openCleaningId && (
        // Its header already names the property: no kicker saying it again.
        <SheetKicker value={null}>
          <CleaningSheet
            key={openCleaningId}
            propertyId={propertyId}
            propertyName={propertyName}
            jobId={openCleaningId}
            timeZone={timeZone}
            onClose={close}
          />
        </SheetKicker>
      )}
    </>
  )
}
